<template>
    <div id="input-workhour-page">
        <pre v-if="isDebug" style="max-height: 200px; overflow: scroll">{{
                { columnDefinitions, lines }
            }}</pre>
        <div v-if="isMobile">{{ trans('pages.input_workhour.csv_import_not_possible') }}</div>
        <div v-else-if="columnHeaders">
            <div class="field">
                <ui5-file-uploader id="workhourfile" @change="onChange">
                    <ui5-button>{{ trans('pages.input_workhour.select_csv_file') }}</ui5-button>
                </ui5-file-uploader>
            </div>
            <div class="parsed-data">
                <table>
                    <thead>
                        <tr style="font-weight: bold">
                            <td>{{ trans('pages.input_workhour.total_lines') }}</td>
                            <td>{{ trans('pages.input_workhour.valid_lines') }}</td>
                            <td>{{ trans('pages.input_workhour.lines_with_errors') }}</td>
                            <td>{{ trans('pages.input_workhour.lines_with_warnings') }}</td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td id="csvCount">{{ csvCount }}</td>
                            <td id="csvCorrect" style="color: forestgreen">
                                {{ csvCorrect }}
                            </td>
                            <td
                                id="csvError"
                                :style="{
                                    color: getColor(csvError ? 'error' : null),
                                }">
                                {{ csvError }}
                            </td>
                            <td
                                id="csvWarning"
                                :style="{
                                    color: getColor(
                                        csvError ? 'warning' : null
                                    ),
                                }">
                                {{ csvWarning }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="read-data" v-if="lines">
                <table border="1" width="100%" style="max-height: 300px">
                    <thead>
                        <th>#</th>
                        <th></th>
                        <th
                            v-for="key in columnHeaders"
                            :key="'heading.' + key">
                            {{ key }}
                        </th>
                        <th></th>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(line, index) in lines"
                            :style="{
                                'text-decoration': line.removed
                                    ? 'line-through'
                                    : 'inherit',
                            }"
                            :key="'preview.line.' + index">
                            <td>{{ line.__nr }}</td>
                            <td>
                                <template
                                    v-for="lineErrorType in [
                                        getErrorType(line),
                                    ].filter((x) => x)">
                                    <template
                                        v-for="toolTipRef in [
                                            'errorToolTip' + index,
                                        ]">
                                        <i
                                            class="fa fa-exclamation-triangle"
                                            @mouseout="
                                                mouseOutTooltip(toolTipRef)
                                            "
                                            @mouseover="
                                                mouseOverTooltip(
                                                    toolTipRef,
                                                    $event.target
                                                )
                                            "
                                            :style="{
                                                color: getColor(lineErrorType),
                                            }" />
                                        <ui5-popover :id="toolTipRef">
                                            <ul>
                                                <li
                                                    v-for="error in line.errors"
                                                    :style="{
                                                        color: errorColors[
                                                            error.type
                                                        ],
                                                    }">
                                                    <strong>
                                                        {{ error.prop }}
                                                    </strong>
                                                    {{ error.error }}
                                                </li>
                                            </ul>
                                            <div>{{ line.__text }}</div>
                                        </ui5-popover>
                                    </template>
                                </template>
                            </td>
                            <td
                                v-for="key in columnHeaders"
                                :contenteditable="!isReadOnly(line, key)"
                                @blur="onBlurContentEditable($event, line, key)"
                                :key="'preview.value.' + index + '.' + key"
                                :style="{
                                    backgroundColor: getBackgroundColor(
                                        line,
                                        key
                                    ),
                                }">
                                {{ getPreviewValue(line, key) }}
                            </td>
                            <td>
                                <i
                                    class="fa"
                                    :class="{
                                        'fa-undo': line.removed,
                                        'fa-trash': !line.removed,
                                    }"
                                    style="cursor: pointer"
                                    @click="
                                        line.removed = !line.removed;
                                        processLines();
                                        lines = [...lines];
                                    " />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <ui5-button
                v-if="lines && lines.length"
                @click="saveToServer"
                :disabled="saving || csvError > 0">
                {{ trans('pages.input_workhour.import') }}
            </ui5-button>
            <ui5-button @click="download">{{ trans('pages.input_workhour.download_data') }}</ui5-button>
            <div v-if="csvError > 0" class="error">
                {{ trans('pages.input_workhour.resolve_errors') }}
            </div>
            <div v-if="backendErrors" class="error">
                {{ backendErrorString }}
            </div>
        </div>
    </div>
</template>
<script>
import employeeApi from '../api/employee';
import { isMobile } from '../helpers/GeneralHelper';
import * as Papa from 'papaparse';
import IsDebugMixin from '../mixins/is-debug';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import localization from '../mixins/localization';

const dateFormat = 'YYYY-MM-DD';
const timeFormat = 'HH:mm:ss';

const colors = {
    warning: 'orange',
    error: 'red',
};

const backgroundColors = {
    warning: 'orange',
    error: '#ff00008f',
};

export default {
    mixins: [IsDebugMixin, localization],
    data() {
        return {
            saving: false,
            columnDefinitions: null,
            lines: null,
            backendErrors: null,
        };
    },
    mounted() {
        employeeApi.fetchImportWorkdayColumns().then((response) => {
            this.columnDefinitions = response.data.data;
        });
    },
    computed: {
        isMobile: () => isMobile(),
        errorColors: () => colors,
        backendErrorString() {
            let error = this.backendErrors;
            if (error && error.data) {
                error = error.data;
                if (error.nr && error.error && error.line) {
                    error =
                        `Lijn ${error.nr}: ${error.error}` +
                        (this.isDebug
                            ? ' - ' + JSON.stringify(error.line)
                            : '');
                }
            }
            return error;
        },
        columnHeaders() {
            if (!this.columnDefinitions) {
                return null;
            }
            return Object.keys(this.columnDefinitions).filter(
                (x) => !x.startsWith('__')
            );
        },
        csvCount() {
            return this.lines ? this.lines.length : '';
        },
        csvError() {
            if (!this.lines) {
                return '';
            }
            return this.lines.filter(
                (x) =>
                    x.errors.filter((x) => x.type == 'error').length > 0 ||
                    x.__parseError
            ).length;
        },
        csvWarning() {
            if (!this.lines) {
                return '';
            }
            return this.lines.filter(
                (x) => x.errors.filter((x) => x.type == 'warning').length > 0
            ).length;
        },
        csvCorrect() {
            if (!this.lines) {
                return '';
            }
            return (
                this.lines.length -
                this.lines.filter((x) => x.errors.length > 0 || x.__parseError)
                    .length
            );
        },
    },
    methods: {
        getCleanData() {
            if (!this.lines || !this.lines.length) {
                return showErrorMessage(this.trans('pages.input_workhour.no_data_to_download'));
            }

            const lines = JSON.parse(
                JSON.stringify(this.lines.filter((x) => !x.removed))
            );
            lines.forEach((line) => {
                Object.keys(line).forEach((key) => {
                    if (key == 'errors' || key.startsWith('__')) {
                        delete line[key];
                    }
                });
            });
            return lines;
        },
        download() {
            const lines = this.getCleanData();

            const csv = Papa.unparse(lines);

            const blob = new Blob([csv], {
                type: 'text/csv;charset=utf-8;',
            });

            saveAs(blob, 'workhours.csv');
        },
        onBlurContentEditable($event, line, key) {
            const text = $event.target.innerText;
            line[key] = text;
            this.processLine(line);
        },
        mouseOutTooltip(toolTipRef) {
            this.$el.querySelector('#' + toolTipRef).close();
        },
        mouseOverTooltip(toolTipRef, target) {
            this.$el.querySelector('#' + toolTipRef).showAt(target);
        },
        getErrorType(line, key) {
            let errors = line.errors;
            if (key) {
                errors = errors.filter((x) => x.prop == key);
            }

            if (errors.find((x) => x.type == 'error')) {
                return 'error';
            }

            if (errors.find((x) => x.type == 'warning')) {
                return 'warning';
            }
            return null;
        },
        getColor(errorType) {
            return colors[errorType];
        },
        getBackgroundColor(line, key) {
            const errorType = this.getErrorType(line, key);
            return backgroundColors[errorType];
        },
        isReadOnly(line, key) {
            const column = this.columnDefinitions[key];
            return column.type == 'label';
        },
        getPreviewValue(line, key) {
            const column = this.columnDefinitions[key];
            const rawVal = line[key];
            if (rawVal === undefined && column.type == 'label') {
                const val = parseInt(line[column.for]);
                const options = this.columnDefinitions.__dicts[column.options];
                return options[val] || '';
            }
            return rawVal;
        },
        onChange(changeEvent) {
            this.csvErrorMsgs = [];
            this.lines = [];

            const files = changeEvent.target.files;
            for (let i = 0, f; (f = files[i]); i++) {
                const reader = new FileReader();

                reader.onload = (() => {
                    return (loadEvent) => {
                        this.parseCsvWorkhourFile(loadEvent.target.result);
                    };
                })(f);

                reader.readAsText(f);
            }
        },
        parseCsvWorkhourFile(filetext) {
            filetext = filetext.trim();
            const parsed = Papa.parse(filetext, { header: true });
            const textLines = filetext.split(parsed.meta.linebreak);
            const lines = parsed.data;
            this.columnHeaders.forEach((key) => {
                if (!parsed.meta.fields.includes(key)) {
                    this.csvErrorMsgs.push({
                        lineError: false,
                        text: '',
                        error: this.trans('pages.input_workhour.column_missing', { column: key }),
                    });
                }
            });

            lines.forEach((line, index) => {
                line.__text = textLines[index + 1];
                line.__nr = index + 1;
                line.__parseError = null;
                line.__removed = false;
                line.errors = [];
            });

            if (parsed.errors && parsed.errors.length) {
                parsed.errors.forEach((error) => {
                    const line = lines[error.row];
                    line.__parseError = error;
                });
            }
            this.lines = lines;
            this.processLines();
            //this.saveCsvWorkdaysToServer(lines);
        },
        processLines() {
            this.lines.forEach((line) => {
                this.processLine(line);
            });
        },
        processLine(line) {
            const errors = [];
            if (!line.removed) {
                Object.entries(this.columnDefinitions).forEach(
                    ([columnKey, columnInfo]) => {
                        if (columnKey.startsWith('__')) {
                            return;
                        }
                        if (columnInfo.type == 'label') {
                            return;
                        }
                        const val = line[columnKey];
                        if (
                            !columnInfo.required &&
                            (val === undefined || val === null || !val.length)
                        ) {
                            return;
                        }
                        if (columnInfo.type == 'date.y-m-d') {
                            const date = moment.utc(val, dateFormat, true);
                            const valid = date.isValid();
                            if (!valid) {
                                errors.push({
                                    prop: columnKey,
                                    error: this.trans('pages.input_workhour.date_format_error', { format: dateFormat }),
                                    type: 'error',
                                });
                            }
                        } else if (columnInfo.type == 'time.h:m:s') {
                            const time = moment.utc(val, timeFormat, true);
                            const valid = time.isValid();
                            if (!valid) {
                                errors.push({
                                    prop: columnKey,
                                    error: this.trans('pages.input_workhour.time_format_error', { format: timeFormat }),
                                    type: 'error',
                                });
                            }
                        } else if (columnInfo.type == 'id') {
                            const optionsKey = columnInfo.options;

                            const options =
                                this.columnDefinitions.__dicts[optionsKey];

                            const optionKeys = Object.keys(options);
                            if (!optionKeys.includes(val)) {
                                errors.push({
                                    prop: columnKey,
                                    error: this.trans('pages.input_workhour.invalid_id'),
                                    type: 'error',
                                });
                            } else {
                                const option = options[val] || '';
                                const labelVal = line[columnInfo.for];
                                if (option != labelVal) {
                                    errors.push({
                                        prop: columnKey,
                                        error: this.trans('pages.input_workhour.id_label_mismatch', { id: columnKey, label: columnInfo.for }),
                                        type: 'warning',
                                    });
                                }
                            }
                        } else {
                            console.error('line to validate', {
                                line,
                                columnKey,
                                columnInfo,
                            });
                        }
                    }
                );
            }
            line.errors = errors;
        },
        saveToServer() {
            const lines = this.getCleanData();
            this.saving = true;
            this.backendErrors = null;
            employeeApi
                .importWorkday(lines)
                .then((response) => {
                    showInfoMessage(this.trans('pages.input_workhour.import_success'));
                })
                .catch((error) => {
                    showErrorMessage(this.trans('pages.input_workhour.import_error'));
                    this.backendErrors = error.response.data;
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>
